import { QueueProgress } from './queue'

/**
 * A download in flight, as the UI sees it. The registry row is where a finished download lives; this is
 * only what is happening right now, which no database can hold: a page that opens mid-download still has
 * to find the progress and the way to stop it.
 */
export interface ActiveDownload {
	progress: QueueProgress
	cancel: () => void
	/** Move these stops, and what follows them, to the front — what opening a stop asks for. */
	prioritizeStops: (stopIds: number[]) => void
}

interface Controls {
	cancel: () => void
	prioritizeStops: (stopIds: number[]) => void
}

const active = new Map<string, ActiveDownload>()
const listeners = new Set<() => void>()

const notify = (): void => {
	for (const listener of [...listeners]) listener()
}

const replace = (key: string, changes: Partial<ActiveDownload>): void => {
	const current = active.get(key)
	if (!current) return

	// A new object each time, so `useSyncExternalStore` sees the change; the same one otherwise, so it
	// does not re-render on every unrelated notification.
	active.set(key, { ...current, ...changes })
	notify()
}

export const subscribeToDownloads = (listener: () => void): (() => void) => {
	listeners.add(listener)

	return () => {
		listeners.delete(listener)
	}
}

export const activeDownload = (key: string): ActiveDownload | undefined => active.get(key)

export const activeDownloadKeys = (): string[] => [...active.keys()]

/**
 * Claim the key before the first fetch. The manifest has to be read before there is a queue to control,
 * and a visitor who taps download and sees nothing happen taps again.
 */
export const beginDownload = (key: string): boolean => {
	if (active.has(key)) return false

	active.set(key, { progress: { done: 0, total: 0 }, cancel: () => cancelPending(key), prioritizeStops: () => {} })
	notify()

	return true
}

const pendingCancels = new Set<string>()

const cancelPending = (key: string): void => {
	pendingCancels.add(key)
}

/** Hand over the real controls once the queue exists, honouring a cancel that arrived before it did. */
export const attachDownload = (key: string, controls: Controls): void => {
	replace(key, controls)

	if (pendingCancels.delete(key)) controls.cancel()
}

export const reportProgress = (key: string, progress: QueueProgress): void => replace(key, { progress })

export const endDownload = (key: string): void => {
	pendingCancels.delete(key)
	if (active.delete(key)) notify()
}
